import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, message, Spin } from 'antd';
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';

interface StripePaymentFormProps {
  paymentIntent: {
    id: string;
    client_secret: string;
    amount: number;
    currency: string;
  };
  registrationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  paymentIntent,
  registrationId,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    setLoading(true);

    try {
      const { error, paymentIntent: confirmedPaymentIntent } = await stripe.confirmCardPayment(
        paymentIntent.client_secret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (error) {
        message.error(`Payment failed: ${error.message}`);
      } else if (confirmedPaymentIntent?.status === 'succeeded') {
        message.success('Payment successful! Setting up your business...');

        // Complete the registration
        const response = await apiFetch(`${API_URL}/registration/complete/${registrationId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          onSuccess();
        } else {
          message.error('Payment succeeded but registration failed. Please contact support.');
        }
      }
    } catch (error: any) {
      message.error('Payment error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <h3>💳 Complete Your Payment</h3>
        <p>Secure payment powered by Stripe</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Card Details
          </label>
          <div style={{
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            background: 'white'
          }}>
            <CardElement
              options={cardStyle}
              onChange={(event) => setCardComplete(event.complete)}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px', textAlign: 'center' }}>
          <strong>Total: £{(paymentIntent.amount / 100).toFixed(2)}</strong>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            onClick={onCancel}
            style={{ flex: 1 }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            style={{ flex: 1 }}
            disabled={!stripe || !cardComplete || loading}
            loading={loading}
          >
            {loading ? 'Processing...' : 'Pay Now'}
          </Button>
        </div>
      </form>

      <div style={{ marginTop: '16px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
        <p>🔒 Your payment information is encrypted and secure.</p>
        <p>Test card: 4242 4242 4242 4242 (any future expiry, any CVC)</p>
      </div>
    </div>
  );
};

export default StripePaymentForm;